/* eslint-disable no-console */
import { useEffect, useMemo, useRef } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { isIntTooLongError, isRecordNotFoundError } from '#src/lib/utils.mjs';

import { useCreateNotification } from '../hooks';
import { logger } from '../lib/logger';
import { setJsonItem } from '../lib/session-storage/json';
import { trackStartMembershipOrder } from '../lib/order/trackStartMembershipOrder';
import { toCheckout } from '../store/location/actions';

const startMembershipOrderMutation = gql`
  mutation StartMembershipOrder(
    $items: [OrderItemInput!]!
    $referringGameId: Int
    $timeZone: String!
    $userId: Int!
  ) {
    startOrder: startOrderV2(
      items: $items
      referringGameId: $referringGameId
      resetOrder: true
      timeZone: $timeZone
      userId: $userId
    ) {
      id

      orderItems {
        id
        type
        amount

        ... on MembershipOrderItem {
          estimatedNetRevenue
          sku
        }
      }
    }
  }
`;

async function startMembershipOrder({
  gameId,
  onError,
  product,
  startOrderMutation,
  userId,
  offerId,
  cartVariant,
  onInvalidOfferId,
  isNewTrialFunnel,
  onMutationInitiated,
  orderItemInputs,
  dispatch,
  version,
}) {
  console.log('startMembershipOrder called');
  try {
    if (onInvalidOfferId && !offerId) return onInvalidOfferId();

    console.log('startMembershipOrder: onMutationInitiated()');
    onMutationInitiated();

    const {
      data: { startOrder: order },
    } = await startOrderMutation();

    trackStartMembershipOrder({
      gameId,
      order,
      userId,
    });

    if (isNewTrialFunnel)
      setJsonItem('IS_NEW_TRIAL_FUNNEL_' + order?.id, 'TRUE');

    dispatch(
      toCheckout({
        product,
        orderId: order.id,
        cartVariant,
        orderItemInputs,
        version, // AB Test from formsort/contentful
      })
    );
  } catch (error) {
    logger.alarm(error, {}, 'Failed to create membership order.');
    onError(error);
    return;
  }
}

const buildOrderItems = ({
  gameId,
  gameType,
  offerId,
  paymentPlan,
  promoCode,
}) => {
  const items = [
    {
      Membership: {
        gameType,
        membershipOfferId: parseInt(offerId),
        paymentPlan,
      },
    },
  ];

  if (promoCode) {
    items.push({ Promo: { code: promoCode } });
  }

  if (gameId) {
    items.push({
      Game: {
        gameId,
        gameType: gameType == 'waybetter' ? 'falkor' : gameType,
        paymentPlan: 'optional',
      },
    });
  }

  return items;
};

/**
 * This hook does not check for membership status. That check must be done
 * in the component using this hook (e.g. MembershipSignup or StartMembershipOrder),
 * via useRedirectEffect, before calling useStartOrder.
 *
 * @param {object} root0
 * @param {*} root0.gameId
 * @param {*} root0.offerId
 * @param {*} root0.cartVariant
 * @param {*} root0.paymentPlan
 * @param {*} root0.product
 * @param {*} root0.promoCode
 * @param {*} root0.skip
 * @param {*} root0.timeZone
 * @param {*} root0.isNewTrialFunnel
 * @param {*} root0.userId
 * @param {*} root0.onInvalidOfferId
 * @param {*} root0.version
 * @returns {{ error: any }}
 */
export const useStartMembershipOrder = ({
  gameId,
  offerId,
  cartVariant,
  paymentPlan,
  product,
  promoCode,
  skip,
  timeZone,
  isNewTrialFunnel,
  userId,
  onInvalidOfferId,
  version,
}) => {
  // prevent extra renders from triggering more mutations
  const mutationInitiatedRef = useRef(false);

  // we encode these into the cart url so we can recreate the order if a different user hits the cart url.
  // this can occur when a prospect user switches browsers or logs in using a different account.
  // see https://waybetter.atlassian.net/browse/WBRT-2171
  const orderItemInputs = useMemo(
    () => ({
      gameId,
      gameType: product,
      offerId,
      paymentPlan,
      promoCode,
    }),
    [gameId, product, offerId, paymentPlan, promoCode]
  );

  const [startOrderMutation, { loading, error }] = useMutation(
    startMembershipOrderMutation,
    {
      variables: {
        items: buildOrderItems(orderItemInputs),
        timeZone,
        userId,
        referringGameId: gameId,
      },
    }
  );

  console.log(
    'startMembershipOrder mutationInitiatedRef: ',
    mutationInitiatedRef
  );
  console.log('startMembershipOrder mutation: loading: ', loading);
  console.log('startMembershipOrder mutation: error: ', error);
  const createNotification = useCreateNotification();

  const dispatch = useDispatch();

  useEffect(() => {
    console.log('useStartMembershipOrder: useEffect() function called');
    if (skip || mutationInitiatedRef.current || loading) return;

    const onError = (error) => {
      createNotification({ message: error.message, status: 'error' });
      if (
        onInvalidOfferId &&
        (isIntTooLongError(error) || isRecordNotFoundError(error))
      )
        onInvalidOfferId();
    };

    startMembershipOrder({
      gameId,
      onError,
      onMutationInitiated: () => (mutationInitiatedRef.current = true),
      product,
      startOrderMutation,
      userId,
      offerId,
      cartVariant,
      onInvalidOfferId,
      isNewTrialFunnel,
      orderItemInputs,
      dispatch,
      version,
    });
  }, [
    createNotification,
    dispatch,
    gameId,
    mutationInitiatedRef,
    loading,
    offerId,
    cartVariant,
    onInvalidOfferId,
    isNewTrialFunnel,
    product,
    skip,
    startOrderMutation,
    orderItemInputs,
    userId,
    version,
  ]);

  return { error };
};
